import { BoneMesh } from "./BoneMesh.tsx"
import { Entity, Hit, noHit } from "./Entity.ts"
import { Mannequin, Skeleton } from "./Mannequin.tsx"
import { Pose } from "./Pose.ts"
import { meshHitTest } from "./triangleHitTest.ts"
import { mat4, Ray, vec3, vec4 } from "./VectorMath.ts"

export class PosedMannequin implements Entity {
  private _worldFromMesh_posedTransforms: mat4[] | null = null
  private _deformedMeshPositions: vec3[] | null = null

  constructor(
    readonly mannequin: Mannequin,
    readonly pose: Pose,
  ) { }

  /**
   * @returns The transforms needed to perform linear blend skinning on my mesh.
   */
  worldFromMesh_posedTransforms(): mat4[] {
    if (this._worldFromMesh_posedTransforms !== null) {
      return this._worldFromMesh_posedTransforms
    }
    const { bones } = this.mannequin.skeleton
    const bindFromMesh = this.mannequin.skinBinding.bindFromMesh
    this._worldFromMesh_posedTransforms = new PosedSkeleton(this.mannequin.skeleton, this.pose)
      .worldFromBone_posedTransforms()
      .map((worldFromBone, b) => worldFromBone
        .times(bones[b].boneFromBind)
        .times(bindFromMesh)
      )
    return this._worldFromMesh_posedTransforms
  }

  hitTest(ray: Ray): Hit {
    if (this._deformedMeshPositions === null) {
      const worldFromMesh_posedTransforms = this.worldFromMesh_posedTransforms()
      const { boneIndexes, boneWeights } = this.mannequin.skinBinding
      this._deformedMeshPositions = [...vec3.yieldVec3s(this.mannequin.mesh.positions).map((pMesh3, v) =>
        linearBlendSkinningDeformation(pMesh3, v, worldFromMesh_posedTransforms, boneIndexes, boneWeights)
      )]
    }
    return meshHitTest(this, this._deformedMeshPositions.values(), ray)
  }
}

function linearBlendSkinningDeformation(pMesh3: vec3, v: number, worldFromMesh_posedTransforms: mat4[], boneIndexes: Int32Array, boneWeights: Float32Array): vec3 {
  const pMesh = pMesh3.asPoint
  const bb = v * 4 // Each vertex gets four consecutive slots in boneIndexes and boneWeights.
  let pWorld = vec4.origin
  for (let i = 0; i < 4; ++i) {
    const w = boneWeights[bb + i]
    if (w === 0) {
      continue
    }
    const b = boneIndexes[bb + i]
    pWorld = pWorld.plus(worldFromMesh_posedTransforms[b].times(pMesh).times(w))
  }
  return pWorld.xyz
}

export class PosedSkeleton implements Entity {
  constructor(
    readonly skeleton: Skeleton,
    readonly pose: Pose,
  ) { }

  /**
   * @returns For each bone, the transform from that bone's posed space to world space. COLLADA calls this a "joint matrix". In the COLLADA skinning equation it's JMi.
   */
  worldFromBone_posedTransforms(): mat4[] {
    const { bones, topologicalOrder } = this.skeleton
    let transforms: mat4[] = Array(bones.length).fill(mat4.identity)
    for (let i of topologicalOrder) {
      const bone = bones[i]
      const bonePose = this.pose.bonePoses[i].mat4
      const worldFromParent = bone.parentIndex === -1
        ? mat4.identity
        : transforms[bone.parentIndex]
      transforms[i] = worldFromParent.times(bone.parentFromBone).times(bonePose)
    }
    return transforms
  }

  /**
   * @returns For each bone, the transform to scale and position the standard bone mesh in world space, for drawing the bones.
   */
  worldFromBoneMesh_posedTransforms(): mat4[] {
    const { bones } = this.skeleton
    return this.worldFromBone_posedTransforms().map((unscaled, i) => unscaled.times(bones[i].boneMeshTransform))
  }

  hitTest(ray: Ray): Hit {
    let best = noHit
    const worldFromBoneMesh_posedTransforms = this.worldFromBoneMesh_posedTransforms()
    this.skeleton.bones.forEach((_bone, b) => {
      const worldFromBoneMesh = worldFromBoneMesh_posedTransforms[b]
      const worldMesh = vec3.yieldVec3s(BoneMesh.positions).map(pMesh3 => worldFromBoneMesh.times(pMesh3.asPoint).xyz)
      let hit = meshHitTest(this, worldMesh, ray)
      if (hit.when < best.when) {
        hit.extra = b
        best = hit
      }
    })
    return best
  }

  /**
   * @param extra The `extra` value I stored in the `Hit` I returned from `hitTest`.
   * @returns The index of the `Bone` corresponding to `extra`.
   */
  pickBone(extra: any): number | null {
    if (typeof extra === 'number' && extra >= 0 && extra < this.skeleton.bones.length) {
      return extra
    } else {
      return null
    }
  }
}

